import React from 'react';
import { MenuItem, Select } from '@mui/material';
import { taxonomyLabel, taxonomyShortLabel } from './mappingModel';

interface TaxonomyCategorySelectProps {
  'aria-label': string;
  /** The store's own choice; empty means "use the default". */
  value: string;
  /** The GID that applies while the value is empty. */
  defaultGid: string | null;
  defaultLabelPrefix?: string;
  /** Names the default in place of `defaultGid`'s label, for a default that is not a category ("Don't set"). */
  defaultName?: string;
  options: Array<{ gid: string; label: string }>;
  onChange: (gid: string) => void;
}

/** One Shopify standard category choice; the first entry is the resolved default, never a blank. */
const TaxonomyCategorySelect: React.FC<TaxonomyCategorySelectProps> = ({
  value,
  defaultGid,
  defaultLabelPrefix = 'Default',
  defaultName,
  options,
  onChange,
  ...rest
}) => {
  const known = options.some((option) => option.gid === value);
  const defaultText = defaultName ?? (defaultGid ? taxonomyShortLabel(taxonomyLabel(options, defaultGid)) : 'none');
  return (
    <Select
      size="small"
      fullWidth
      displayEmpty
      value={value}
      onChange={(event) => onChange(String(event.target.value))}
      inputProps={{ 'aria-label': rest['aria-label'] }}
      renderValue={(selected) =>
        selected ? taxonomyShortLabel(taxonomyLabel(options, String(selected))) : `${defaultLabelPrefix}: ${defaultText}`
      }
      sx={{ fontSize: 13, '& .MuiSelect-select': { py: 0.75 } }}
    >
      <MenuItem value="">{`${defaultLabelPrefix}: ${defaultText}`}</MenuItem>
      {value && !known && <MenuItem value={value}>{taxonomyLabel(options, value)}</MenuItem>}
      {options.map((option) => (
        <MenuItem key={option.gid} value={option.gid}>{option.label}</MenuItem>
      ))}
    </Select>
  );
};

export default TaxonomyCategorySelect;
